import { useEffect, useRef } from "react";
import { useSession } from "@/hooks/useSession";

const POLL_INTERVAL_MS = 12_000; // centre of the 10-15s spec window

/**
 * Drop-in replacement for `useSession` that polls the backend every 12s
 * while the tab is visible. Replaces the manual "Refresh status" button
 * on the applicant flow.
 *
 * Stop conditions:
 *  - `stop === true` (callers pass `submission.readyForReview` here)
 *  - tab hidden (`document.visibilityState !== "visible"`)
 *  - component unmounts
 *
 * On visibility regaining the tab, we refresh once immediately so the
 * applicant doesn't see stale state.
 *
 * Server-side rate limit on the polling endpoint is a separate backend
 * follow-up (see docs/CLAUDE-applicant-submission-eligibility.md §10.3).
 */
export function useSessionPolling(
  token: string | undefined,
  options: { stop?: boolean } = {},
) {
  const base = useSession(token);
  const refreshRef = useRef(base.refresh);
  refreshRef.current = base.refresh;

  const stopped = !token || options.stop === true;

  useEffect(() => {
    if (stopped) return;

    let intervalId: number | null = null;

    const startInterval = () => {
      if (intervalId !== null) return;
      intervalId = window.setInterval(() => {
        if (document.visibilityState === "visible") {
          void refreshRef.current();
        }
      }, POLL_INTERVAL_MS);
    };

    const stopInterval = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void refreshRef.current();
        startInterval();
      } else {
        stopInterval();
      }
    };

    if (document.visibilityState === "visible") startInterval();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      stopInterval();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [stopped]);

  return base;
}
